import { Cause, Effect, Exit } from "effect"
import type { YieldWrap } from "effect/Utils"
import { writable } from "svelte/store"
import type { EffectErrors, EffectResult } from "./effect-type-helpers"
import { Toasts } from "../toasts"
import { friendlyError } from "../friendly-error"
import * as Sentry from "@sentry/svelte"

export class Loading {
    get isLoading(): true {
        return true
    }
    get isOk(): false {
        return false
    }
    get isErr(): false {
        return false
    }
    get error(): undefined {
        return undefined
    }
    get value(): undefined {
        return undefined
    }
}

export class Ok<T> {
    get isLoading(): false {
        return false
    }
    get isOk(): true {
        return true
    }
    get isErr(): false {
        return false
    }
    get error(): undefined {
        return undefined
    }
    constructor(public readonly value: T) {}
}

export class Err<E> {
    get value(): undefined {
        return undefined
    }
    get isLoading(): false {
        return false
    }
    get isOk(): false {
        return false
    }
    get isErr(): true {
        return true
    }
    constructor(public readonly error: E) {}
}

export type Result<T, E> = Ok<T> | Err<E>
export type LoadResult<T, E> = Ok<T> | Err<E> | Loading

export type TagOfError<E> = E extends { _tag: string } ? E["_tag"] : never

export interface ResultifyOptions<E> {
    /**
     * Error to include as toast
     * @default false
     */
    toast?: boolean
    /**
     * Bypass certain error tags for telemetry and toasting
     */
    bypass?: TagOfError<E>[]
    /**
     * Send error to telemetry
     * @default true
     */
    telemetry?: boolean
}
/**
 *
 * @param effect
 * @param params optional tooltip error message
 * @returns
 */
export async function resultify<T, E>(
    effect: Effect.Effect<T, E, never>,
    { toast = false, bypass = [], telemetry = true }: ResultifyOptions<E> = {},
): Promise<Result<T, E>> {
    const good = await Effect.runPromiseExit(effect)
    if (Exit.isSuccess(good)) {
        return new Ok(good.value)
    } else {
        const cause = good.cause
        if (Cause.isFailType(cause)) {
            const err = cause.error
            if (!isBypassed(err, bypass)) {
                if (toast) Toasts.error(friendlyError(cause.error))
                if (telemetry) {
                    console.error(cause.error)
                    Sentry.captureException(cause.error)
                }
            }

            return new Err(err)
        } else {
            throw cause
        }
    }
}

export interface LoadifyOptions<E> extends ResultifyOptions<E> {
    /**
     * Debug logs
     */
    debug?: boolean
    /**
     * Lazy loading
     */
    lazy?: boolean
}

/**
 *
 * @param effect The effect to loadify
 * @param params Whether to loadify lazily, and whether to toast the error
 * @returns A store with a rerun() to rerun the effect
 */
export function loadify<T extends Effect.Effect<unknown, unknown, never>>(
    effect?: T,
    {
        lazy = false,
        toast = false,
        debug = false,
        telemetry = true,
        bypass = [],
    }: LoadifyOptions<EffectErrors<T>> = {},
) {
    if (debug) console.log("loadify init", effect)
    type A = EffectResult<T>
    type E = EffectErrors<T>
    const { subscribe, set } = writable<LoadResult<A, E>>(new Loading())
    let iter = 0
    const runEffect = (effect: T) => {
        iter++
        if (debug) console.log("loadify running with iteration ", iter)
        const thisIter = iter
        set(new Loading())
        resultify<A, E>(effect as Effect.Effect<A, E>, { toast, bypass, telemetry }).then((rtn) => {
            if (iter === thisIter) {
                if (debug) console.log("loadify done #", iter)
                set(rtn as Result<A, E>)
            } else {
                if (debug) console.log("loadify cancelled with iteration ", iter, " of ", thisIter)
            }
        })
    }
    if (effect && !lazy) {
        if (debug) console.log("loadify running initial effect")
        runEffect(effect)
    }
    const subscribeLazy: typeof subscribe = (...args) => {
        if (iter === 0 && effect) {
            // Will only run if lazy due to iter
            if (debug) console.trace("loadify subscriber added - lazy subscriber initiated")
            runEffect(effect)
        } else {
            if (debug) console.log("loadify subscriber added")
        }
        return subscribe(...args)
    }
    return {
        subscribe: lazy ? subscribeLazy : subscribe,
        rerun: (newEffect: T) => {
            effect = newEffect
            runEffect(newEffect)
        },
        refresh: () => effect && runEffect(effect),
    }
}

export async function resultigen<
    Eff extends YieldWrap<Effect.Effect<unknown, unknown, never>>,
    AEff,
>(
    f: (resume: Effect.Adapter) => Generator<Eff, AEff, never>,
): Promise<
    Result<
        AEff,
        [Eff] extends [never]
            ? never
            : [Eff] extends [YieldWrap<Effect.Effect<infer _A, infer E, infer _R>>]
              ? E
              : never
    >
> {
    try {
        const gen = Effect.gen(f)
        // @ts-expect-error bypassing to not lose my head
        const good = await Effect.runPromise(gen)
        return new Ok(good)
    } catch (err) {
        // @ts-expect-error bypassing to not lose my head
        return new Err(err)
    }
}

const hasTag = (err: unknown): err is { _tag: string } => {
    return !!(err && typeof err === "object" && "_tag" in err && typeof err._tag === "string")
}

const isBypassed = (err: unknown, bypassList: string[]) => {
    if (!hasTag(err)) return false
    return bypassList.includes(err._tag)
}
